import React, {useEffect} from 'react';
import {Routes} from './routes/';
import './App.css';

import {AuthenticatedTemplate, UnauthenticatedTemplate, useMsal} from "@azure/msal-react";
import {handleLogout, msalUtils} from "./MsalUtils";
import {BrowserRouter as Router, Route, Switch} from "react-router-dom";
import {loadToonimoTrainingContent} from "./toonimoUtils";
import {useAppDispatch, useAppSelector} from "./store/hooks";
import {selectReleaseToggles, setReleaseToggles} from "./ReleaseToggles/releaseTogglesSlice";
import {resourcesApiClient} from "./Resources/ResourcesApiClient";
import {MenuStructureResponse} from "./Resources/models/AdviceLibrary";
import {setAdviceLibraryMenu, setPracticeDocumentsMenu} from "./Resources/menuStructureSlice";
import {MeetingPortal} from "./MeetingPortal/MeetingPortal";
import RelayProvider from "./ClientManagement/Meeting/Relay/RelayProvider";
import ReportingPrintView from "./Reporting/ReportingPrintView";
import {fetchAndSetReleaseToggles} from "./utils/LaunchDarklyReleaseTogglesUtils";
import {pacingUserRedirect} from "./PacingUserRedirect";
import {RouteTracker} from "./routes/RouteTracker";
import {useIdleLogout} from "./hooks/useIdleLogout";
import {initializeAppInsights} from "./AppInsights";
import {fetchSpaConfigKeyVaultAndUpdateSessionStorage} from "./core/storageHelper";
import {App_Conf} from "./core/app_conf";
import {setIsNationalSampleAdmin} from "./Authorization/nationalSampleAdminSlice";
import {setReleaseTogglesGetter} from "./core/api";


const App = () => {
    const msal = useMsal();
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        setReleaseTogglesGetter(() => releaseToggles);
    }, [releaseToggles]);

    // Logout after 90 minutes of inactivity (app-side idle timeout),
    // 90 mins because the meeting portal authenticator has limit for 90mins
    useIdleLogout({
        timeoutMs: 90 * 60 * 1000,
        enabled: true,
        onIdle: async () => {
            await handleLogout(msal);
        }
    });

    return (
        <div>
            <Router>
                <RelayProvider>
                    <Switch>
                        <Route exact path="/ReportPreview/:id" component={ReportingPrintView}/>
                        <Route exact path="/meetings" component={MeetingPortal}/>
                        <Route render={() =>
                            <>
                                <UnauthenticatedTemplate>
                                    {() => msalUtils.handleLogin(msal)}
                                </UnauthenticatedTemplate>
                                <AuthenticatedTemplate>
                                    <InitialLoad/>
                                    <AppRoutes/>
                                </AuthenticatedTemplate>
                            </>
                        }/>
                    </Switch>
                </RelayProvider>
            </Router>
        </div>);
};

const AppRoutes = () => (
    <>
        {/* Do not remove RouteTracker, it is needed for AppInsights page view tracking */}
        <RouteTracker/>
        <Routes/>
    </>
);

export const InitialLoad = () => {
    const dispatch = useAppDispatch();
    const msal = useMsal();

    useEffect(() => {
        (async () => {
            try {
                const token = await msalUtils.acquireToken(msal);
                const groups = msalUtils.getGroupsFromToken(token);
                const isNavigatedToPacingModel = await pacingUserRedirect(msal, groups);
                if (isNavigatedToPacingModel) return;

                const isNationalSampleAdmin = Array.isArray(groups)
                    && App_Conf.NATIONAL_SAMPLE_ADMIN_GROUP.length > 0
                    && groups.includes(App_Conf.NATIONAL_SAMPLE_ADMIN_GROUP);
                dispatch(setIsNationalSampleAdmin(isNationalSampleAdmin));
            } catch (error) {
                dispatch(setIsNationalSampleAdmin(false));
            }

            try {
                await fetchSpaConfigKeyVaultAndUpdateSessionStorage();
                initializeAppInsights();
            } catch (error) {
                console.error("Failed to fetch config from client-management", error);
            }

            fetchAndSetReleaseToggles()
                .then((releaseToggles) => {
                    if (releaseToggles) {
                        dispatch(setReleaseToggles(releaseToggles));
                        loadToonimoTrainingContent();
                    }
                });

            resourcesApiClient.getMenuStructure(false)
                .then((menuStructureResponse: MenuStructureResponse) => {
                    dispatch(setAdviceLibraryMenu(menuStructureResponse.menu["AdviceLibrary"]));
                    dispatch(setPracticeDocumentsMenu(menuStructureResponse.menu["PracticeDocuments"]));
                })
                .catch(console.error);
        })();
    }, []);

    return <></>;
};
export default App;